import { PropertyInputs, CalculationResults, ProjectionDataPoint } from '../types';
import { safeDivide, safePercentage, safeMultiplyDivide, ensureValidNumber } from './safeMath';

interface YearMetrics {
  jahr: number;
  cashflow: number;
  cashflowVorSteuer: number;
  restschuld: number;
  immobilienwert: number;
  eigentumsanteil: number;
  vermoegen: number;
  eigenkapitalrendite: number;
  gebaudewertAfA: number;
  cumulativeInstandhaltung: number;
  zinsenJahr: number;
  tilgungJahr: number;
  mieteinnahmenJahr?: number;
  hausgeldJahr?: number;
  instandhaltungJahr?: number;
  zinsJahr?: number;
  afaJahr?: number;
  steuerJahr?: number;
  monatlicherCashflowVorSteuer?: number;
  monatlicherCashflowNachSteuer?: number;
}

interface LastYearData {
  restschuld: number;
  immobilienwert: number;
  gebaudewertAfA: number;
  eigenkapitalBedarf: number;
  darlehenssumme: number;
  cumulativeInstandhaltung?: number;
  cumulativeCashflowNachSteuer?: number;
  cumulativeZinsen?: number;
}

const calculateMetricsForYear = (
  year: number,
  calculationInputs: PropertyInputs,
  lastYearData: LastYearData
): YearMetrics => {
  const {
    wohnflaeche,
    mietentwicklungProJahr,
    inflationProJahr,
    mietausfallrisiko,
    instandhaltung,
    instandhaltungJ10,
    zinssatz,
    zinssatzJ10,
    tilgung,
    steuersatz,
    afaSatz,
    hausgeldNichtUmlagefaehig,
    investitionenJahr10
  } = calculationInputs;
  
  const { restschuld: restschuldStartOfYear, immobilienwert: immobilienwertStartOfYear } = lastYearData;
  
  // Ensure stellplatzMiete is properly handled
  const stellplatzMiete = calculationInputs.stellplatzTyp !== 'Nein' 
    ? (calculationInputs.stellplatzMiete || 0) 
    : 0;
  
  const baseJahresmiete = (wohnflaeche * calculationInputs.kaltmieteProQm * 12) + (stellplatzMiete * 12);
  const currentJahresKaltmiete = baseJahresmiete * Math.pow(1 + safeDivide(mietentwicklungProJahr, 100), year - 1);
  // const currentJahresUmlagefaehigHausgeld = (hausgeldUmlagefaehig * 12) * Math.pow(1 + safeDivide(inflationProJahr, 100), year - 1);
  const currentJahresNichtUmlagefaehigHausgeld = (hausgeldNichtUmlagefaehig * 12) * Math.pow(1 + safeDivide(inflationProJahr, 100), year - 1);
  
  const activeInstandhaltung = year > 10 ? instandhaltungJ10 : instandhaltung;
  const currentJahresInstandhaltung = (wohnflaeche * activeInstandhaltung) * Math.pow(1 + safeDivide(inflationProJahr, 100), year - 1);
  
  const cumulativeInstandhaltung = (lastYearData.cumulativeInstandhaltung || 0) + currentJahresInstandhaltung;
  const currentJahresMietausfall = safeMultiplyDivide(currentJahresKaltmiete, mietausfallrisiko, 100);
  
  const currentZinssatz = year > 10 ? zinssatzJ10 : zinssatz;
  
  // Calculate interest and repayment with minimum 1% repayment rule
  const konstanteJahresrate = lastYearData.darlehenssumme * (safeDivide(zinssatz, 100) + safeDivide(tilgung, 100));
  const zinsenJahr = restschuldStartOfYear > 0.01 ? restschuldStartOfYear * safeDivide(currentZinssatz, 100) : 0;
  
  // Calculate minimum repayment (1% of current debt)
  const mindestTilgung = restschuldStartOfYear * 0.01;
  
  // Calculate standard repayment based on constant rate
  const standardTilgung = konstanteJahresrate - zinsenJahr;
  
  // Choose the higher of the two repayments, but not more than the remaining debt
  let tilgungJahr = Math.max(standardTilgung, mindestTilgung);
  tilgungJahr = Math.min(tilgungJahr, restschuldStartOfYear);
  
  // Ensure no negative values
  tilgungJahr = Math.max(0, tilgungJahr);
  
  const restschuldEndeOfYear = Math.max(0, restschuldStartOfYear - tilgungJahr);
  
  // The actual bank rate is now variable
  const jahresBankRate = zinsenJahr + tilgungJahr;
  
  let gebaudewertAfA = lastYearData.gebaudewertAfA;
  if (year === 10) {
    gebaudewertAfA += investitionenJahr10;
  }
  const jaehrlicheAfa = safeMultiplyDivide(gebaudewertAfA, afaSatz, 100);
  const operatingExpenses = currentJahresNichtUmlagefaehigHausgeld + currentJahresInstandhaltung + currentJahresMietausfall;
  let cashflowVorSteuer = currentJahresKaltmiete - operatingExpenses - jahresBankRate;
  
  const steuerpflichtigeEinnahmen = currentJahresKaltmiete;
  let steuerlichAbziehbareAusgaben = currentJahresNichtUmlagefaehigHausgeld + currentJahresMietausfall + zinsenJahr + jaehrlicheAfa;
  
  if (year === 1 && calculationInputs.sofortabschreibungAktivieren) {
    steuerlichAbziehbareAusgaben += calculationInputs.anfangsinvestitionen;
  }
  
  const einkommensteuerGrundlage = steuerpflichtigeEinnahmen - steuerlichAbziehbareAusgaben;
  const steuerbelastung = safeMultiplyDivide(einkommensteuerGrundlage, steuersatz, 100);
  let cashflowNachSteuer = cashflowVorSteuer - steuerbelastung;
  
  let currentImmobilienwert = immobilienwertStartOfYear * (1 + safeDivide(calculationInputs.wertzuwachsProJahr, 100));
  if (year === 10) {
    currentImmobilienwert += investitionenJahr10;
    cashflowNachSteuer -= investitionenJahr10;
  }
  
  const immobilienwertZuwachs = currentImmobilienwert - immobilienwertStartOfYear;
  const eigentumsanteil = currentImmobilienwert - restschuldEndeOfYear;
  const nettovermoegen = eigentumsanteil - lastYearData.eigenkapitalBedarf;
  
  let eigenkapitalrendite = 0;
  if (lastYearData.eigenkapitalBedarf > 0.01) {
    eigenkapitalrendite = safePercentage(immobilienwertZuwachs + tilgungJahr + cashflowNachSteuer, lastYearData.eigenkapitalBedarf);
  }
  
  return {
    jahr: year,
    cashflow: cashflowNachSteuer,
    cashflowVorSteuer: cashflowVorSteuer,
    restschuld: restschuldEndeOfYear,
    immobilienwert: currentImmobilienwert,
    eigentumsanteil,
    vermoegen: nettovermoegen,
    eigenkapitalrendite: ensureValidNumber(eigenkapitalrendite),
    gebaudewertAfA,
    cumulativeInstandhaltung,
    zinsenJahr,
    tilgungJahr,
    mieteinnahmenJahr: currentJahresKaltmiete,
    hausgeldJahr: currentJahresNichtUmlagefaehigHausgeld,
    instandhaltungJahr: currentJahresInstandhaltung,
    zinsJahr: zinsenJahr,
    afaJahr: jaehrlicheAfa,
    steuerJahr: steuerbelastung,
    monatlicherCashflowVorSteuer: safeDivide(cashflowVorSteuer, 12),
    monatlicherCashflowNachSteuer: safeDivide(cashflowNachSteuer, 12)
  };
};

export const calculateSingleProperty = (inputs: PropertyInputs): CalculationResults => {
  // Ensure zero values don't break calculations
  const safeInputs = { ...inputs };
  Object.keys(safeInputs).forEach(key => {
    const value = (safeInputs as any)[key];
    if (typeof value === 'number' && (value === 0 || value < 0.01)) {
      // Keep legitimate zeros, but prevent division errors
      if (['wohnflaeche', 'inseratpreis', 'angebotspreis', 'finanzierungsanteil'].includes(key)) {
        (safeInputs as any)[key] = Math.max(0.01, value);
      }
    }
  });
  
  // IST calculations
  const ist_nebenkosten = safeMultiplyDivide(safeInputs.inseratpreis, safeInputs.makler + safeInputs.notar + safeInputs.grundbuchamt + safeInputs.grunderwerbst, 100);
  const ist_gesamtinvestition = safeInputs.inseratpreis + ist_nebenkosten + safeInputs.anfangsinvestitionen;
  const ist_darlehen = safeMultiplyDivide(safeInputs.inseratpreis, safeInputs.finanzierungsanteil, 100);
  const ist_ekBedarf = ist_gesamtinvestition - ist_darlehen;
  const ist_monatlicheRate = ist_darlehen > 0.01 ? safeDivide(ist_darlehen * (safeDivide(safeInputs.zinssatz, 100) + safeDivide(safeInputs.tilgung, 100)), 12) : 0;
  
  const ist_calc_inputs = {
    ...safeInputs,
    kaltmieteProQm: safeInputs.istKaltmieteProQm,
    stellplatzMiete: safeInputs.istStellplatzMiete
  };
  
  const ist_year1_metrics = calculateMetricsForYear(1, ist_calc_inputs, {
    restschuld: ist_darlehen,
    immobilienwert: safeInputs.inseratpreis + safeInputs.anfangsinvestitionen,
    gebaudewertAfA: safeMultiplyDivide(safeInputs.inseratpreis, safeInputs.gebaudeanteil, 100) + safeInputs.anfangsinvestitionen,
    eigenkapitalBedarf: ist_ekBedarf,
    darlehenssumme: ist_darlehen,
  });
  
  // SOLL calculations
  const soll_nebenkosten = safeMultiplyDivide(safeInputs.angebotspreis, safeInputs.makler + safeInputs.notar + safeInputs.grundbuchamt + safeInputs.grunderwerbst, 100);
  const soll_gesamtinvestition = safeInputs.angebotspreis + soll_nebenkosten + safeInputs.anfangsinvestitionen;
  const soll_darlehen = safeMultiplyDivide(safeInputs.angebotspreis, safeInputs.finanzierungsanteil, 100);
  const soll_ekBedarf = soll_gesamtinvestition - soll_darlehen;
  const soll_monatlicheRate = soll_darlehen > 0.01 ? safeDivide(soll_darlehen * (safeDivide(safeInputs.zinssatz, 100) + safeDivide(safeInputs.tilgung, 100)), 12) : 0;
  
  const soll_calc_inputs = {
    ...safeInputs,
    stellplatzMiete: (safeInputs.stellplatzTyp !== 'Nein' ? safeInputs.stellplatzMiete : 0)
  };
  
  const projectionData: ProjectionDataPoint[] = [];
  
  const anfangsAfaBasis = safeMultiplyDivide(safeInputs.angebotspreis, safeInputs.gebaudeanteil, 100);
  const afaBasis = safeInputs.sofortabschreibungAktivieren ? anfangsAfaBasis : anfangsAfaBasis + safeInputs.anfangsinvestitionen;
  
  let lastYearData: LastYearData & { cumulativeCashflowNachSteuer: number; cumulativeZinsen: number } = {
    restschuld: soll_darlehen,
    immobilienwert: safeInputs.angebotspreis + safeInputs.anfangsinvestitionen,
    gebaudewertAfA: afaBasis,
    eigenkapitalBedarf: soll_ekBedarf,
    darlehenssumme: soll_darlehen,
    cumulativeCashflowNachSteuer: -soll_ekBedarf,
    cumulativeInstandhaltung: 0,
    cumulativeZinsen: 0
  };
  
  // Year 0
  projectionData.push({
    jahr: 0,
    ...lastYearData,
    cashflow: 0,
    cashflowVorSteuer: 0,
    vermoegen: lastYearData.immobilienwert - lastYearData.restschuld - soll_ekBedarf,
    eigentumsanteil: lastYearData.immobilienwert - lastYearData.restschuld,
    eigenkapitalrendite: 0,
    zinsenJahr: 0,
    tilgungJahr: 0,
    cumulativeZinsen: 0
  });
  
  // Years 1-40
  for (let year = 1; year <= 40; year++) {
    const currentYearMetrics = calculateMetricsForYear(year, soll_calc_inputs, lastYearData);
    lastYearData = {
      ...lastYearData,
      ...currentYearMetrics,
      cumulativeCashflowNachSteuer: lastYearData.cumulativeCashflowNachSteuer + currentYearMetrics.cashflow,
      cumulativeZinsen: lastYearData.cumulativeZinsen + currentYearMetrics.zinsenJahr
    };
    projectionData.push({
      ...currentYearMetrics,
      cumulativeCashflowNachSteuer: lastYearData.cumulativeCashflowNachSteuer,
      cumulativeZinsen: lastYearData.cumulativeZinsen,
      darlehenssumme: lastYearData.darlehenssumme,
      eigenkapitalBedarf: lastYearData.eigenkapitalBedarf
    });
  }
  
  const soll_year1_metrics = projectionData[1];
  const ist_jahreskaltmiete = (safeInputs.wohnflaeche * safeInputs.istKaltmieteProQm * 12) + (safeInputs.istStellplatzMiete * 12);
  
  return {
    eigenkapital: soll_ekBedarf,
    monatlicheRate: soll_monatlicheRate,
    nebenkosten: soll_nebenkosten,
    nebenkostenProzent: safeInputs.makler + safeInputs.notar + safeInputs.grundbuchamt + safeInputs.grunderwerbst,
    gesamtinvestition: soll_gesamtinvestition,
    darlehenssumme: soll_darlehen,
    jahresmiete: (safeInputs.wohnflaeche * safeInputs.kaltmieteProQm * 12) + ((safeInputs.stellplatzTyp !== 'Nein' ? safeInputs.stellplatzMiete : 0) * 12),
    bruttomietrendite: safePercentage((safeInputs.wohnflaeche * safeInputs.kaltmieteProQm * 12) + ((safeInputs.stellplatzTyp !== 'Nein' ? safeInputs.stellplatzMiete : 0) * 12), safeInputs.angebotspreis),
    jaehrlicheAfa: safeMultiplyDivide((safeMultiplyDivide(safeInputs.angebotspreis, safeInputs.gebaudeanteil, 100) + safeInputs.anfangsinvestitionen), safeInputs.afaSatz, 100),
    inseratpreisProQm: safeDivide(safeInputs.inseratpreis, safeInputs.wohnflaeche),
    angebotspreisProQm: safeDivide(safeInputs.angebotspreis, safeInputs.wohnflaeche),
    hausgeldTotal: safeInputs.hausgeldUmlagefaehig + safeInputs.hausgeldNichtUmlagefaehig,
    preisreduktion: safePercentage(safeInputs.inseratpreis - safeInputs.angebotspreis, safeInputs.inseratpreis),
    kaufpreisIst: safeInputs.inseratpreis,
    quadratmeterpreisIst: safeDivide(safeInputs.inseratpreis, safeInputs.wohnflaeche),
    bruttomietrenditeIst: safePercentage(ist_jahreskaltmiete, safeInputs.inseratpreis),
    monatlicheKaltmieteIst: (safeInputs.wohnflaeche * safeInputs.istKaltmieteProQm) + safeInputs.istStellplatzMiete,
    eigenkapitalIst: ist_ekBedarf,
    monatlicheRateIst: ist_monatlicheRate,
    monatlicherCashflowJahr1IstVorSteuer: safeDivide(ist_year1_metrics.cashflowVorSteuer, 12),
    monatlicherCashflowJahr1IstNachSteuer: safeDivide(ist_year1_metrics.cashflow, 12),
    eigenkapitalrenditeJahr1Ist: ist_year1_metrics.eigenkapitalrendite,
    kaufpreisSoll: safeInputs.angebotspreis,
    quadratmeterpreisSoll: safeDivide(safeInputs.angebotspreis, safeInputs.wohnflaeche),
    bruttomietrenditeSoll: safePercentage((safeInputs.wohnflaeche * safeInputs.kaltmieteProQm * 12) + ((safeInputs.stellplatzTyp !== 'Nein' ? safeInputs.stellplatzMiete : 0) * 12), safeInputs.angebotspreis),
    monatlicheKaltmieteSoll: safeDivide((safeInputs.wohnflaeche * safeInputs.kaltmieteProQm * 12) + ((safeInputs.stellplatzTyp !== 'Nein' ? safeInputs.stellplatzMiete : 0) * 12), 12),
    eigenkapitalSoll: soll_ekBedarf,
    monatlicheRateSoll: soll_monatlicheRate,
    monatlicherCashflowJahr1SollVorSteuer: safeDivide(soll_year1_metrics.cashflowVorSteuer, 12),
    monatlicherCashflowJahr1SollNachSteuer: safeDivide(soll_year1_metrics.cashflow, 12),
    eigenkapitalrenditeJahr1Soll: soll_year1_metrics.eigenkapitalrendite,
    projectionData: projectionData,
    // Additional commonly used properties
    cashflowMonatlichNachSteuer: safeDivide(soll_year1_metrics.cashflow, 12),
    eigenkapitalrendite: soll_year1_metrics.eigenkapitalrendite,
    gesamtkosten: soll_gesamtinvestition,
    kaufpreisFaktor: safeDivide(safeInputs.angebotspreis, safeInputs.wohnflaeche * safeInputs.kaltmieteProQm * 12),
    anfaenglicheTilgung: safeInputs.tilgung
  };
};

export const calculateIRR = (cashflows: number[]): number => {
  const maxIterations = 1000;
  const tolerance = 0.00001;
  let rate = 0.1;
  
  for (let i = 0; i < maxIterations; i++) {
    let npv = 0;
    let dnpv = 0;
    
    for (let j = 0; j < cashflows.length; j++) {
      const pv = safeDivide(cashflows[j], Math.pow(1 + rate, j));
      npv += pv;
      dnpv -= safeDivide(j * cashflows[j], Math.pow(1 + rate, j + 1));
    }
    
    const newRate = rate - safeDivide(npv, dnpv);
    
    if (Math.abs(newRate - rate) < tolerance) {
      return newRate * 100;
    }
    
    rate = newRate;
  }
  
  return rate * 100;
};